import { useCallback, useRef, useState } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import { useSyncSmartFolder } from './mutations/useSyncSmartFolder.ts';
import {
	getRecoverableSyncAnalyticsAttributes,
	getSyncErrorType,
	type SmartFolderRecoverableSyncProvider,
	type SmartFolderRecoverableSyncSourceSurface,
} from './SmartFolderRecoverableSyncAnalytics.ts';
import { useFolderSyncActions } from './SmartFolderSyncContext.tsx';
import {
	SmartFolderErrorState,
	getSyncErrorState,
	type SmartFolderErrorState as SmartFolderErrorStateType,
} from './SmartFolderErrorConstants.ts';

export type SmartFolderSyncStatus = 'idle' | 'syncing' | 'success' | 'error';

type SmartFolderAutoSyncResult = 'success' | 'failed' | 'skippedTtl';

const AUTO_SYNC_RESULT_SUCCESS: SmartFolderAutoSyncResult = 'success';
const AUTO_SYNC_RESULT_FAILED: SmartFolderAutoSyncResult = 'failed';
const AUTO_SYNC_RESULT_SKIPPED_TTL: SmartFolderAutoSyncResult = 'skippedTtl';

type SmartFolderAutoSyncTriggerSurface = 'folderView' | 'ancestorView';

type SmartFolderAutoSyncProvider = SmartFolderRecoverableSyncProvider;

export type SmartFolderSyncTriggerOptions = {
	/** Bypass the backend TTL/no-op path and ask the server to check remote state now. */
	force?: boolean;
	/** Surface that explicitly triggered this sync attempt. */
	sourceSurface?: SmartFolderRecoverableSyncSourceSurface;
};

type UseSmartFolderSyncOptions = {
	/** Called after a successful sync so consumers can refresh their data (e.g. page tree, folder view). */
	onSyncComplete?: () => void;
	/** Surface that triggered the automatic sync. */
	triggerSurface?: SmartFolderAutoSyncTriggerSurface;
	/** Journey ID forwarded from the route query params, when present. */
	journeyId?: string | null;
	/**
	 * External provider backing the smart folder, when known.
	 * TODO: plumb provider from sync call sites
	 */
	provider?: SmartFolderAutoSyncProvider;
	/** External container app backing the smart folder, when known. */
	containerApp?: string;
	/** Prevent all sync requests while persisted automatic sync is paused. */
	isSyncPaused?: boolean;
};

type UseSmartFolderSyncResult = {
	/** Trigger a sync for the given folder. Safe to call multiple times – concurrent calls for the same folder are de-duplicated. */
	triggerSync: (folderId: string, options?: SmartFolderSyncTriggerOptions) => void;
	/** Current sync status. */
	status: SmartFolderSyncStatus;
	/**
	 * A UI-intent token set when a sync mutation response fails with a known
	 * smart-folder error or falls back to the generic transient error.
	 * Maps directly to a SmartFolderError screen variant. Null when there is no
	 * mutation response error UI to show, such as successful syncs or network rejects.
	 */
	syncErrorState: SmartFolderErrorStateType | null;
};

const SMART_FOLDER_ANALYTICS_SOURCE = 'smartFolder';

const getAutoSyncResult = ({
	errors,
	success,
}: {
	errors: ReadonlyArray<{ message?: string | null }> | null | undefined;
	success: boolean;
}): SmartFolderAutoSyncResult => {
	const wasSkippedDueToTtl =
		errors?.some(({ message }) => message?.toLowerCase().includes('ttl')) ?? false;

	if (success && wasSkippedDueToTtl) {
		return AUTO_SYNC_RESULT_SKIPPED_TTL;
	}

	return success ? AUTO_SYNC_RESULT_SUCCESS : AUTO_SYNC_RESULT_FAILED;
};

/**
 * Hook that triggers a smart-folder sync and exposes the current sync status.
 *
 * Designed to be called when a user opens a folder view or navigates to
 * content within a smart folder. Duplicate concurrent calls for the same
 * folder are de-duplicated. The syncing folder ID is published via
 * SmartFolderSyncContext so other components (e.g. the page tree) can show a
 * loading indicator.
 */
export const useSmartFolderSync = (
	options?: UseSmartFolderSyncOptions,
): UseSmartFolderSyncResult => {
	const {
		onSyncComplete,
		triggerSurface = 'folderView',
		journeyId,
		provider = 'unknown',
		containerApp,
		isSyncPaused = false,
	} = options ?? {};
	const { syncSmartFolder, loading } = useSyncSmartFolder();
	const { addSyncingFolder, removeSyncingFolder } = useFolderSyncActions();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const syncingFolderRef = useRef<string | null>(null);
	const statusRef = useRef<SmartFolderSyncStatus>('idle');
	const retryCountByFolderRef = useRef<Map<string, number>>(new Map());
	const [syncErrorState, setSyncErrorState] = useState<SmartFolderErrorStateType | null>(null);
	// Derive a simple status from the mutation state + our local tracking
	const status: SmartFolderSyncStatus = loading ? 'syncing' : statusRef.current;

	const fireAutoSyncAnalytics = useCallback(
		({
			durationMs,
			folderId,
			isRetry,
			result,
			retryCount,
			sourceSurface,
			success,
			syncErrorType,
		}: {
			durationMs: number;
			folderId: string;
			isRetry: boolean;
			result: SmartFolderAutoSyncResult;
			retryCount?: number;
			sourceSurface: SmartFolderRecoverableSyncSourceSurface;
			success: boolean;
			syncErrorType?: string;
		}) => {
			const now = Date.now();
			const journeyAttribute = journeyId ? { journeyId } : {};
			createEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SMART_FOLDER_ANALYTICS_SOURCE,
					action: 'completed',
					actionSubject: 'smartFolderAutoSync',
					attributes: {
						folderId,
						provider,
						containerApp: containerApp ?? 'unknown',
						triggerSurface,
						sourceSurface,
						result,
						durationMs,
						...journeyAttribute,
					},
				},
			}).fire();

			createEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SMART_FOLDER_ANALYTICS_SOURCE,
					action: 'completed',
					actionSubject: 'smartFolderRecoverableSync',
					attributes: {
						...getRecoverableSyncAnalyticsAttributes({
							containerApp,
							durationMs,
							folderId,
							isRetry,
							now,
							provider,
							retryCount,
							sourceSurface,
							success,
							syncErrorType,
						}),
						...journeyAttribute,
					},
				},
			}).fire();
		},
		[createEvent, journeyId, provider, triggerSurface, containerApp],
	);

	const triggerSync = useCallback(
		(folderId: string, triggerOptions?: SmartFolderSyncTriggerOptions) => {
			if (isSyncPaused) {
				return;
			}

			// De-duplicate: don't fire another request if we're already syncing this folder
			if (syncingFolderRef.current === folderId) {
				return;
			}

			const startedAt = Date.now();
			syncingFolderRef.current = folderId;
			statusRef.current = 'syncing';
			setSyncErrorState(null);
			addSyncingFolder(folderId);

			const { force, sourceSurface = force ? 'manual-retry' : 'folder-view-auto-sync' } =
				triggerOptions ?? {};
			const isRetry = Boolean(force);
			const retryCount = isRetry
				? (retryCountByFolderRef.current.get(folderId) ?? 0) + 1
				: undefined;
			if (retryCount !== undefined) {
				retryCountByFolderRef.current.set(folderId, retryCount);
			}

			syncSmartFolder({ folderId, ...(force !== undefined ? { force } : {}) })
				.then((result) => {
					const payload = result?.data?.confluence_syncSmartFolder;
					const success = payload?.success ?? false;
					const syncResult = getAutoSyncResult({ errors: payload?.errors, success });
					const syncErrorType = getSyncErrorType(payload?.errors);
					statusRef.current = syncResult === 'success' ? 'success' : 'error';

					fireAutoSyncAnalytics({
						durationMs: Date.now() - startedAt,
						folderId,
						isRetry,
						result: syncResult,
						retryCount,
						sourceSurface,
						success,
						syncErrorType,
					});

					if (syncResult === AUTO_SYNC_RESULT_SUCCESS) {
						onSyncComplete?.();
					} else {
						// Prefer a mapped BE error state, but show generic retry UI for failed
						// mutation responses that do not include a FE-known errorType.
						const structuredErrorState =
							typeof syncErrorType === 'string' ? getSyncErrorState(syncErrorType) : null;
						const fallbackErrorState =
							syncResult === AUTO_SYNC_RESULT_FAILED
								? SmartFolderErrorState.SyncTransientError
								: null;
						setSyncErrorState(structuredErrorState ?? fallbackErrorState);
					}
				})
				.catch(() => {
					statusRef.current = 'error';
					fireAutoSyncAnalytics({
						durationMs: Date.now() - startedAt,
						folderId,
						isRetry,
						result: 'failed',
						retryCount,
						sourceSurface,
						success: false,
					});
				})
				.finally(() => {
					const finishedId = syncingFolderRef.current;
					syncingFolderRef.current = null;
					if (finishedId) {
						removeSyncingFolder(finishedId);
					}
					statusRef.current = 'idle';
				});
		},
		[
			syncSmartFolder,
			addSyncingFolder,
			fireAutoSyncAnalytics,
			isSyncPaused,
			onSyncComplete,
			removeSyncingFolder,
		],
	);

	return { triggerSync, status, syncErrorState };
};
